import type UserSession from '../model/UserSession'

// Kept in step with the fuller enum in model/User.ts, which is what drives the role dropdown
// and the permission map. The two have drifted before (model/User.ts also carries `manager`);
// a role only needs to be here if something in this file gates on it.
export enum userRolesBase {
  doctor = 'doctor',
  nurse = 'nurse',
  medTech = 'medTech',
  socialWorker = 'social-worker',
  technicalAssistant = 'technical-assistant',
  specialist = 'specialist',
  supplyManager = 'supplyManager',
  interpreter = 'interpreter',
}

// Who runs the exam report: the people who order and perform exams. Doctors and nurses order them
// and follow up on the results, so they read the same report the medtech performs against.
export const canViewExaminationsReport = (userSession: UserSession): boolean =>
  userSession.admin ||
  [userRolesBase.medTech, userRolesBase.doctor, userRolesBase.nurse].includes(
    userSession.role as userRolesBase,
  )

/**
 * Who sees the supply counts.
 *
 * The supply manager owns the stock, and is the only role that reads the counts back.
 * See patient-backend/docs/supply-checklist.md.
 *
 * Narrowed from "admin or medTech or supplyManager". This report is the one screen in the EHR
 * that is not scoped by patient groups — it is keyed on facility — so a wide audience here
 * meant a wide audience over every site in the deployment. The medtech files counts on the
 * tablet and does not need to query them across facilities; an admin has no stock
 * responsibility. Both keep the exam report, which is group-scoped.
 *
 * Server side this is enforced in ehr-server/src/services/reports.ts, and the facility list
 * behind the filter is scoped to the caller's own organisation — the browser hiding a tab was
 * never the protection.
 */
export const canViewSupplyReports = (userSession: UserSession): boolean =>
  userSession.superAdmin || userSession.role === userRolesBase.supplyManager

/**
 * Who edits the checklist itself — which items the medtech is asked to count.
 *
 * Admins are included deliberately: someone has to be able to fix the list before the first
 * supply-manager account exists, and after it is locked out.
 */
export const canManageSupplyChecklist = (userSession: UserSession): boolean =>
  userSession.admin || userSession.role === userRolesBase.supplyManager

/**
 * Whether the /reports route and its sidebar entry render at all. A user who can reach any one
 * tab can reach the page; Reports.tsx decides which tabs they actually get.
 */
export const canViewReports = (userSession: UserSession): boolean =>
  canViewExaminationsReport(userSession) || canViewSupplyReports(userSession)

/**
 * A supply manager has no patient access at all, so the patients dashboard the app normally
 * lands on shows them "No Permission". HospitalRun redirects `/` for them instead.
 */
export const isSupplyManager = (userSession: UserSession): boolean =>
  userSession.role === userRolesBase.supplyManager

/**
 * An interpreter only reads ultrasound / ECG / skin studies. They get no sidebar, a navbar without
 * the case counters, and land on their worklist instead of the patients dashboard.
 */
export const isInterpreter = (userSession: UserSession): boolean =>
  userSession.role === userRolesBase.interpreter
